/**
 * Utility functions for ViewAllNotificationsPage
 */

import type { SeverityChipProps } from './types';

/**
 * Maps severity levels to Material-UI chip properties for visual indication.
 * Only critical and high severity levels get special styling.
 */
export function getSeverityChipProps(
  severity?: string,
): SeverityChipProps | undefined {
  const normalized = String(severity ?? '').toLowerCase();
  if (normalized === 'critical') return { label: 'CRITICAL', color: 'error' };
  if (normalized === 'high') return { label: 'HIGH', color: 'error' };
  return undefined;
}

/**
 * Converts ISO date string to human-readable relative time (e.g., "5m ago", "2h ago", "3d ago").
 */
export function formatRelativeTime(isoDate?: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function formatInboxTimestamp(isoDate?: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  ) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes(),
    ).padStart(2, '0')}`;
  }

  if (diffDays <= 2) {
    return formatRelativeTime(isoDate);
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const currentYear = now.getFullYear();

  if (date.getFullYear() === currentYear) {
    return `${day}/${month}`;
  }

  return `${day}/${month}/${String(date.getFullYear()).slice(-2)}`;
}
